import { useState, useEffect, createContext, useContext } from 'react';
import api from '../../services/api';
import { wendeAppSchriftAn } from '../../utils/appSchrift';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { TabBar } from './TabBar';
import MobileTabBar from './MobileTabBar';
import { DemoBanner } from '../common/DemoBanner';
import FloatingNotepad from '../FloatingNotepad';
import { PWAInstallBanner } from '../PWAInstallBanner';
import { MentionHoverCard } from '../MentionHoverCard';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';
import useStudentDesign from '../../hooks/useStudentDesign';
import { useAuth } from '../../contexts/AuthContext';
import ShortcutsHelpDialog from '../ShortcutsHelpDialog';
import GlobalEmailComposer from '../email/GlobalEmailComposer';
import GlobalWindowManager from '../common/GlobalWindowManager';

// Mobile Sidebar Context (used by Header hamburger on tablets)
const MobileSidebarContext = createContext();

export function useMobileSidebar() {
  return useContext(MobileSidebarContext);
}

// Wendet die im Firmenprofil > Design gewählten App-Farben (Sidebar-Navigation,
// Header, Modal-Kopfzeilen) als CSS-Custom-Properties auf <html> an. Fällt auf
// die in index.css definierten Defaults (Schwarz) zurück, falls kein Wert gesetzt ist.
function applyAppTheme(profile = {}) {
  const root = document.documentElement;
  if (profile.app_nav_color) root.style.setProperty('--ew-nav-bg', profile.app_nav_color);
  if (profile.app_header_color) root.style.setProperty('--ew-header-bg', profile.app_header_color);
  if (profile.app_modal_header_color) root.style.setProperty('--ew-modal-header-bg', profile.app_modal_header_color);
  // Modal-Hintergrund: Farbe frei wählbar (Default Grau), ausgeschaltet = weiß
  if (typeof profile.app_modal_background === 'boolean' || profile.app_modal_background_color) {
    const bg = profile.app_modal_background === false ? '#ffffff' : (profile.app_modal_background_color || '#e6e6e6');
    root.style.setProperty('--ew-modal-card-bg', bg);
  }
  // Schriftart/-größe der App (Firmenprofil › Design)
  wendeAppSchriftAn(profile.app_font_family, profile.app_font_size);
}

const LS_NAV_EINGEKLAPPT = 'ew_hauptnav_eingeklappt';

export function Layout({ children }) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { helpOpen, setHelpOpen, shortcuts } = useKeyboardShortcuts();
  // EasyWork Student: schwarzes Handy-Design und eigenes App-Icon
  const { user } = useAuth();
  useStudentDesign(user);

  const toggleMobileSidebar = () => setIsMobileSidebarOpen(!isMobileSidebarOpen);
  const closeMobileSidebar = () => setIsMobileSidebarOpen(false);

  // Hauptnavigation am Desktop ein-/ausklappen (z. B. auf kleinen Laptops mehr Platz für Listen); wird gemerkt
  const [isNavEingeklappt, setIsNavEingeklappt] = useState(() => {
    try { return localStorage.getItem(LS_NAV_EINGEKLAPPT) === '1'; } catch { return false; }
  });
  const toggleNav = () => setIsNavEingeklappt((v) => {
    try { localStorage.setItem(LS_NAV_EINGEKLAPPT, v ? '0' : '1'); } catch { /* ignore */ }
    return !v;
  });

  // App-Farben aus TenantProfile laden und live aktualisieren (Settings > Firmenprofil > Design)
  useEffect(() => {
    let cancelled = false;
    api.get('/tenant-profile').then((r) => {
      const profile = r.data?.profile || r.data || {};
      if (!cancelled) applyAppTheme(profile);
    }).catch(() => { /* Defaults aus index.css greifen */ });

    const handler = (ev) => applyAppTheme(ev?.detail || {});
    window.addEventListener('tenant-profile-updated', handler);
    return () => { cancelled = true; window.removeEventListener('tenant-profile-updated', handler); };
  }, []);

  return (
    <MobileSidebarContext.Provider value={{ isMobileSidebarOpen, toggleMobileSidebar, closeMobileSidebar, isNavEingeklappt, toggleNav }}>
      {/* Skip-to-content Link für Keyboard/Screenreader-User */}
      <a href="#main-content" className="skip-to-content">Zum Hauptinhalt springen</a>
      {/* 100dvh statt 100vh: auf dem iPhone ist 100vh höher als der sichtbare Bereich; safe-x hält im Querformat Abstand zur Notch */}
      <div className="flex flex-col h-screen h-[100dvh] bg-gray-50 safe-x">
        {/* Demo Mode Banner */}
        <DemoBanner />

        <div className="flex flex-1 overflow-hidden">
          {/* Desktop Sidebar - hidden on mobile/tablet below lg; per Schalter im Header einklappbar */}
          <div className={isNavEingeklappt ? 'hidden' : 'hidden lg:block'} data-testid="hauptnavigation">
            <Sidebar />
          </div>

          {/* Tablet Sidebar Overlay (md <= w < lg) - hamburger driven */}
          {isMobileSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-40 hidden md:block lg:hidden"
              onClick={closeMobileSidebar}
            />
          )}

          {/* Tablet Sidebar - slide in from left (md <= w < lg) */}
          <div className={`
            fixed inset-y-0 left-0 z-50 hidden md:block lg:hidden
            transform transition-transform duration-300 ease-in-out
            ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}>
            <Sidebar onNavigate={closeMobileSidebar} />
          </div>

          <div className="flex flex-1 flex-col overflow-hidden">
            <Header />
            <TabBar />
            <main id="main-content" className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-4 lg:pb-6" style={{ overflowAnchor: 'none' }}>
              {children}
            </main>
          </div>
        </div>

        {/* Mobile Bottom Tab Bar (<768px) */}
        <MobileTabBar />

        {/* PWA Install Banner (mobile only, after 3 visits) */}
        <PWAInstallBanner />

        {/* Floating Notepad — schwebt über allen Pages außer Login & Notes */}
        <FloatingNotepad />

        {/* Globaler Hover-Tooltip für @-Mentions in RichText */}
        <MentionHoverCard />

        {/* Globaler E-Mail-Composer — bleibt beim Tab-Wechsel im DOM */}
        <GlobalEmailComposer />

        {/* Fenster-Manager für Modale mit Window-Controls (Fuhrpark, Buchhaltung, …) */}
        <GlobalWindowManager />

        {/* Tastatur-Shortcuts Hilfe-Dialog (via ?) */}
        <ShortcutsHelpDialog open={helpOpen} onOpenChange={setHelpOpen} shortcuts={shortcuts} />
      </div>
    </MobileSidebarContext.Provider>
  );
}
